import React, { useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useOrderNotifications } from '../hooks/useOrderNotifications';
import './Header.css';

const STAFF_TAP_WINDOW_MS = 450;

export default function Header() {
  const { itemCount } = useCart();
  const { user, isLoggedIn } = useAuth();
  const { unreadCount, markAllSeen } = useOrderNotifications();
  const navigate = useNavigate();
  const pendingPath = localStorage.getItem('active_order_path');
  const headerRef = useRef(null);
  const logoTapsRef = useRef({ count: 0, timer: null });

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => {
      document.documentElement.style.setProperty('--header-height', `${el.offsetHeight}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => () => {
    if (logoTapsRef.current.timer) clearTimeout(logoTapsRef.current.timer);
  }, []);

  // Single tap → home (after a short window). Triple tap → staff launcher.
  const handleBrandClick = useCallback((e) => {
    e.preventDefault();
    const taps = logoTapsRef.current;
    taps.count += 1;
    if (taps.timer) clearTimeout(taps.timer);

    if (taps.count >= 3) {
      taps.count = 0;
      navigate('/staff');
      return;
    }

    taps.timer = setTimeout(() => {
      if (taps.count === 1) navigate('/');
      taps.count = 0;
      taps.timer = null;
    }, STAFF_TAP_WINDOW_MS);
  }, [navigate]);

  return (
    <header className="header" ref={headerRef}>
      <div className="header-inner container">
        <a href="/" className="header-brand" onClick={handleBrandClick}>
          <img src="/logo.png" alt="St Barbara & St Noufer" className="header-logo" />
          <div>
            <h1 className="header-title">St Barbara & St Noufer</h1>
            <p className="header-subtitle">Coptic Orthodox Church</p>
          </div>
        </a>
        <div className="header-actions">
          {isLoggedIn ? (
            <button
              className="header-user"
              onClick={() => {
                markAllSeen();
                navigate('/my-orders');
              }}
              title={unreadCount > 0 ? `${unreadCount} order update${unreadCount === 1 ? '' : 's'}` : 'My orders'}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              {unreadCount > 0 && (
                <span className="header-user-count" aria-label={`${unreadCount} unread`}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          ) : (
            <button
              className="header-signin"
              onClick={() => navigate('/auth')}
            >
              Sign In
            </button>
          )}
          {pendingPath && (
            <button className="header-pending" onClick={() => navigate(pendingPath)} title="View pending order">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              <span className="header-pending-dot" />
            </button>
          )}
          <button className="header-cart" onClick={() => navigate('/cart')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            {itemCount > 0 && <span className="header-cart-count">{itemCount}</span>}
          </button>
        </div>
      </div>
    </header>
  );
}
